import React, { useEffect } from "react";
import { useAppSelector } from "../../../../../hooks/useAppSelector";
import {
  KrookieFormationTypeId,
  setFormDataEditDetails,
} from "../../../../../services/reducers/master-plan-modification";
import { useAppDispatch } from "../../../../../hooks/useAppDispatch";
import {
  FormDataEditDetails,
  KrookieStatus,
} from "../../../../../interfaces/krookie";
import { getPreviousKrookieNum } from "../../../../../services/api";
import { getNewChangeNumber } from "../../../../../utils/krookie-helpers";
import { formatDateWithoutTime } from "../../../../../utils/formatDate";
import { ConvertToLines } from "../../../../../utils/master-plan-helper";
import DetailsEditing from "../details-editing";

const DetailsEditor = () => {
  const {
    cities,
    landUse,
    phase,
    formDataEditDetails,
    krookiePlots,
    selectedCity,
    convertedKrookieData,
    screenshots,
    selectedPlots,
  } = useAppSelector((state) => state.masterPlanModification);

  const { user } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();

  const currentPlot = krookiePlots[0];
  const currentScreenshots = currentPlot
    ? screenshots[currentPlot.objectId]
    : [];
  const currentConvertedKrookieData = currentPlot
    ? convertedKrookieData.krookies[currentPlot.objectId] || null
    : null;
  const currentFormData = currentPlot
    ? formDataEditDetails[currentPlot.objectId] || null
    : null;

  useEffect(() => {
    if (!currentPlot || currentFormData) return;

    const { phaseId, number, estateAr, cityId, landUseId, area, objectId } =
      currentPlot;

    if (currentConvertedKrookieData) {
      const initialFormData = {
        cityEn: {
          displayValue: currentConvertedKrookieData.cityEn.displayValue,
          value: currentConvertedKrookieData.cityEn.value,
        },
        phase: {
          displayValue: currentConvertedKrookieData.phase.displayValue || "",
          value: currentConvertedKrookieData.phase.value,
        },
        landUse: {
          displayValue: currentConvertedKrookieData.landUse.displayValue || "",
          value: currentConvertedKrookieData.landUse.value,
        },
        plotNumber: currentConvertedKrookieData.plotNumber,
        oldPlotNumber: currentConvertedKrookieData.oldPlotNumber,
        area: currentPlot.area,
        cityAr: currentConvertedKrookieData.cityAr,
        buildArea: currentConvertedKrookieData.buildArea,
        height: currentConvertedKrookieData.height,
        front: currentConvertedKrookieData.front,
        back: currentConvertedKrookieData.back,
        side: currentConvertedKrookieData.side,
        streetNumber: currentConvertedKrookieData.streetNumber,
        previousKrookieCode: currentConvertedKrookieData.previousKrookieCode,
        notes: currentConvertedKrookieData.notes,
        qrCodeLink: currentConvertedKrookieData.qrCodeLink,
      };
      dispatch(
        setFormDataEditDetails({
          id: objectId,
          formData: initialFormData,
        }),
      );
    } else {
      const selectedPlotsIds = selectedPlots.map((plot) => plot.objectId);

      const initialFormData = {
        cityEn: { displayValue: cities.get(cityId) || "", value: cityId },
        phase: { displayValue: phase.get(phaseId) || "", value: phaseId },
        landUse: {
          displayValue: landUse.get(landUseId) || "",
          value: landUseId,
        },
        plotNumber: number,
        oldPlotNumber: number,
        area,
        cityAr: estateAr,
        front: "7.5",
        back: "4.5",
        side: "3.5",
        qrCodeLink: `${window.location.origin}${process.env.PUBLIC_URL}/search/plt?city=${selectedCity}&res=true&fid=${krookiePlots[0].fid}`,
      };

      dispatch(
        setFormDataEditDetails({
          id: objectId,
          formData: initialFormData,
        }),
      );

      getPreviousKrookieNum(selectedPlotsIds).then((res) => {
        const previousCode = res ? res.join(", ") : "";

        dispatch(
          setFormDataEditDetails({
            id: objectId,
            formData: { previousKrookieCode: previousCode },
          }),
        );
      });
    }
  }, [
    currentPlot?.objectId,
    currentConvertedKrookieData,
    currentFormData,
    cities,
  ]);

  useEffect(() => {
    if (!currentPlot || !currentFormData) return;

    if (currentFormData.area !== currentPlot.area) {
      dispatch(
        setFormDataEditDetails({
          id: currentPlot.objectId,
          formData: { area: currentPlot.area },
        }),
      );
    }
  }, [currentPlot?.area, currentPlot?.objectId]);

  const requiredFields: (keyof FormDataEditDetails)[] = [
    "cityEn",
    "phase",
    "plotNumber",
    "streetNumber",
    "landUse",
    "buildArea",
    "area",
    "height",
    "front",
    "back",
    "side",
  ];

  if (!currentFormData) {
    return null;
  }

  const revision = `${formatDateWithoutTime(new Date())}_${getNewChangeNumber(currentConvertedKrookieData?.historyLog ?? [])}`;

  const cardKrookieData = {
    [currentPlot.objectId]: {
      ...currentFormData,
      serial: currentFormData.plotNumber,
      previousKrookieCode: currentFormData.previousKrookieCode,
      revision,
      wgs: currentPlot.spatialReference,
      drafterName: user?.userName,
      directorName: "",
      signatureDrafter: formatDateWithoutTime(new Date()),
      signatureDirector: "",
      tableData: ConvertToLines(currentPlot.newGeometry) ?? [],
      screenshots: {
        firstScreenshot: {
          scale: currentScreenshots[0]?.scale,
          url: currentScreenshots[0]?.url,
        },
        secondScreenshot: {
          scale: currentScreenshots[1]?.scale,
          url: currentScreenshots[1]?.url,
        },
      },
    },
  };

  const documentStatus =
    currentConvertedKrookieData?.statusId === KrookieStatus.Rejected
      ? KrookieStatus.Rejected
      : KrookieStatus.Project;

  return (
    <DetailsEditing
      requiredFields={requiredFields}
      cardKrookieData={cardKrookieData}
      krookieFormationTypeId={KrookieFormationTypeId.Edit}
      disabledFields={{
        cityEn: true,
        phase: true,
        oldPlotNumber: true,
        area: true,
      }}
      status={documentStatus}
    />
  );
};

export default DetailsEditor;
